import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase-config';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Fonction pour charger le profil complet depuis l'API
  const loadUserProfile = async (token) => {
    if (!token) return null;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Erreur chargement profil:', error);
      return null;
    }
  };

  // Fonction pour synchroniser le profil utilisateur avec le backend
  const syncUserProfile = async (session) => {
    if (!session?.access_token) return;
    
    try {
      // D'abord synchroniser
      await axios.post(`${API}/auth/sync-profile`, {}, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      // Puis charger le profil complet
      const profile = await loadUserProfile(session.access_token);
      if (profile) {
        setUserProfile({
          ...profile,
          uid: profile.id
        });
      }
      
      console.log('Profil synchronisé avec succès');
    } catch (error) {
      console.log('Sync profil:', error.response?.data || error.message);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (mounted && session?.user) {
          setCurrentUser({ uid: session.user.id, email: session.user.email });
          
          // Charger le profil complet
          const profile = await loadUserProfile(session.access_token);
          if (profile) {
            setUserProfile({
              ...profile,
              uid: profile.id
            });
          } else {
            setUserProfile({ uid: session.user.id, email: session.user.email });
          }
          
          // Synchroniser le profil au chargement
          await syncUserProfile(session);
        }
      } catch (error) {
        console.error('Erreur init session:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event);
      
      if (mounted) {
        if (session?.user) {
          setCurrentUser({ uid: session.user.id, email: session.user.email });
          
          // Charger le profil complet
          const profile = await loadUserProfile(session.access_token);
          if (profile) {
            setUserProfile({
              ...profile,
              uid: profile.id
            });
          } else {
            setUserProfile({ uid: session.user.id, email: session.user.email });
          }
          
          // Synchroniser à chaque connexion
          if (event === 'SIGNED_IN') {
            await syncUserProfile(session);
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
        }
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signup = async (email, password, userData) => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          business_name: userData.business_name,
          owner_name: userData.owner_name
        }
      }
    });
    
    if (authError) throw authError;
    if (!authData.user) throw new Error("Erreur création compte");
    
    if (!authData.session) {
      return { requiresEmailVerification: true, email };
    }
    
    setCurrentUser({ uid: authData.user.id, email: authData.user.email });
    setUserProfile({ uid: authData.user.id, email: authData.user.email });
    
    return { uid: authData.user.id, email: authData.user.email };
  };

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      if (error.message?.includes('Email not confirmed')) {
        throw new Error('Veuillez confirmer votre email avant de vous connecter');
      }
      if (error.message?.includes('Invalid login')) {
        throw new Error('Email ou mot de passe incorrect');
      }
      throw error;
    }
    
    if (!data.user) throw new Error('Erreur de connexion');
    
    setCurrentUser({ uid: data.user.id, email: data.user.email });
    
    // Charger le profil complet après connexion
    const profile = await loadUserProfile(data.session.access_token);
    if (profile) {
      setUserProfile({
        ...profile,
        uid: profile.id
      });
    } else {
      setUserProfile({ uid: data.user.id, email: data.user.email });
    }
    
    return { uid: data.user.id, email: data.user.email };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setCurrentUser(null);
    setUserProfile(null);
  };

  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  // Fonction pour rafraîchir le profil
  const refreshProfile = async () => {
    const token = await getToken();
    if (token) {
      const profile = await loadUserProfile(token);
      if (profile) {
        setUserProfile({
          ...profile,
          uid: profile.id
        });
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    loading,
    getToken,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
